/*
 * JungleScene — the cartoony rainforest backdrop shared by every page.
 * Pure inline SVG so it scales crisply and needs no image assets.
 */

const BARK = "#8a5a2b";
const BARK_DEEP = "#5b3a1c";

/* A single palm frond, drawn pointing right from the origin. */
function Frond({ fill, vein }) {
    return (
        <g>
            <path
                d="M0 0 C 44 -30 106 -34 152 -10 C 106 20 44 26 0 0 Z"
                fill={fill}
                stroke={vein}
                strokeWidth="3"
                strokeLinejoin="round"
            />
            <path
                d="M4 0 C 52 -8 106 -12 150 -11"
                fill="none"
                stroke={vein}
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.7"
            />
            {/* leaflet cuts along the blade */}
            {[28, 58, 88, 116].map((cx, i) => (
                <g key={cx} stroke={vein} strokeWidth="2.4" strokeLinecap="round" opacity="0.45">
                    <line x1={cx} y1={-3 - i} x2={cx + 12} y2={-16 - i * 2} />
                    <line x1={cx} y1={3 + i} x2={cx + 12} y2={14 + i} />
                </g>
            ))}
        </g>
    );
}

/*
 * NOTE: a CSS `transform` (our sway/float animations) replaces an element's SVG
 * `transform` attribute rather than composing with it, so anything animated gets
 * an untransformed wrapper <g> and keeps its placement on an inner <g>.
 */

/* A bunch of three bananas. */
function BananaBunch({ x, y, scale = 1, rotate = 0, className }) {
    const one = (
        <g>
            <path
                d="M -18 -18 C -27 8 -6 29 20 15 C 16 8 14 6 9 6 C -9 14 -17 2 -12 -17 Z"
                fill="#ffd43b"
                stroke="#c98a06"
                strokeWidth="2.6"
                strokeLinejoin="round"
            />
            <path d="M -8 -12 C -11 2 -2 12 10 11" fill="none" stroke="#fff3b8" strokeWidth="2.4" strokeLinecap="round" opacity="0.8" />
            <path d="M -18 -18 L -21 -27" stroke={BARK} strokeWidth="5" strokeLinecap="round" />
            <circle cx="19" cy="14" r="2.8" fill={BARK_DEEP} />
        </g>
    );

    return (
        <g className={className}>
            <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`}>
                <ellipse cx="-16" cy="-26" rx="11" ry="7" fill={BARK} stroke={BARK_DEEP} strokeWidth="2.5" />
                <g transform="translate(-6 2) rotate(-16)">{one}</g>
                <g transform="translate(6 6) rotate(2)">{one}</g>
                <g transform="translate(16 2) rotate(18)">{one}</g>
            </g>
        </g>
    );
}

/* A broad banana-plant leaf, drawn pointing up-right from the origin. */
function BroadLeaf({ x, y, rotate = 0, scale = 1, fill, vein, opacity = 1, className }) {
    return (
        <g className={className}>
            <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
                <path
                    d="M0 0 C 44 -70 148 -104 236 -78 C 184 -4 80 40 0 0 Z"
                    fill={fill}
                    stroke={vein}
                    strokeWidth="4"
                    strokeLinejoin="round"
                />
                <path d="M6 -2 C 74 -26 158 -56 228 -76" fill="none" stroke={vein} strokeWidth="4" strokeLinecap="round" opacity="0.75" />
                {[0, 1, 2, 3, 4].map((i) => (
                    <line
                        key={i}
                        x1={28 + i * 42}
                        y1={-12 - i * 10}
                        x2={52 + i * 42}
                        y2={-46 - i * 11}
                        stroke={vein}
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        opacity="0.4"
                    />
                ))}
            </g>
        </g>
    );
}

/* A rainforest palm: curved trunk plus a crown of fronds. */
function Palm({
    x,
    y,
    scale = 1,
    flip = false,
    leaf = "#1d8c41",
    leafAlt = "#2eb257",
    vein = "#0a4527",
    opacity = 1,
    withBananas = false,
}) {
    const frondAngles = [-176, -142, -108, -74, -38, 6, 188, 152];

    return (
        <g
            transform={`translate(${x} ${y}) scale(${flip ? -scale : scale} ${scale})`}
            opacity={opacity}
        >
            {/* trunk */}
            <path
                d="M -17 0 C -11 -82 -34 -152 -54 -212 L -33 -218 C -9 -152 11 -82 17 0 Z"
                fill={BARK}
                stroke={BARK_DEEP}
                strokeWidth="4"
                strokeLinejoin="round"
            />
            {/* bark rings */}
            {[-28, -62, -96, -128, -160, -190].map((ty, i) => (
                <path
                    key={ty}
                    d={`M ${-15 - i * 0.6 + i * -1.8} ${ty} C ${-6 - i * 2} ${ty - 7} ${4 - i * 2} ${ty - 7} ${13 - i * 3} ${ty}`}
                    fill="none"
                    stroke={BARK_DEEP}
                    strokeWidth="3"
                    strokeLinecap="round"
                    opacity="0.5"
                />
            ))}
            {/* crown */}
            <g transform="translate(-44 -214)">
                {frondAngles.map((angle, i) => (
                    <g key={angle} transform={`rotate(${angle})`}>
                        <Frond fill={i % 2 === 0 ? leaf : leafAlt} vein={vein} />
                    </g>
                ))}
                <circle cx="0" cy="0" r="11" fill={BARK_DEEP} />
                {withBananas && <BananaBunch x={16} y={26} scale={0.8} rotate={8} />}
            </g>
        </g>
    );
}

/* A hanging vine with a leaf pair and (optionally) bananas at the tip. */
function Vine({ x, drop, sway = 40, bananas = true, opacity = 1 }) {
    return (
        <g opacity={opacity}>
            <path
                d={`M ${x} -10 C ${x + sway} ${drop * 0.35} ${x - sway} ${drop * 0.7} ${x + sway * 0.3} ${drop}`}
                fill="none"
                stroke="#15723a"
                strokeWidth="7"
                strokeLinecap="round"
            />
            <ellipse cx={x + sway * 0.55} cy={drop * 0.33} rx="18" ry="9" fill="#2eb257" stroke="#0a4527" strokeWidth="3" transform={`rotate(-18 ${x + sway * 0.55} ${drop * 0.33})`} />
            <ellipse cx={x - sway * 0.5} cy={drop * 0.62} rx="16" ry="8" fill="#55cf74" stroke="#0a4527" strokeWidth="3" transform={`rotate(22 ${x - sway * 0.5} ${drop * 0.62})`} />
            {bananas && <BananaBunch x={x + sway * 0.3} y={drop + 18} scale={0.72} rotate={-6} />}
        </g>
    );
}

export default function JungleScene() {
    return (
        <div className="jungle-scene" aria-hidden="true">
            {/* ---- canopy layer, pinned to the top of the viewport ---- */}
            <svg
                className="scene-canopy"
                viewBox="0 0 1440 240"
                preserveAspectRatio="xMidYMin slice"
                role="presentation"
            >
                <defs>
                    <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#fff6c4" stopOpacity="0.9" />
                        <stop offset="60%" stopColor="#ffe066" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#ffe066" stopOpacity="0" />
                    </radialGradient>
                </defs>

                {/* sun breaking through the leaves */}
                <circle cx="1120" cy="40" r="250" fill="url(#sunGlow)" />
                <circle cx="1120" cy="30" r="66" fill="#fff1a8" opacity="0.7" />

                {/* hanging vines with banana bunches */}
                <Vine x={268} drop={168} sway={34} />
                <Vine x={1172} drop={128} sway={-40} />
                <Vine x={706} drop={96} sway={26} bananas={false} opacity={0.9} />

                {/* big leaves hanging down from the canopy */}
                <BroadLeaf x={-26} y={-16} rotate={74} scale={0.95} fill="#0f5f33" vein="#042415" className="svg-sway" />
                <BroadLeaf x={104} y={-24} rotate={104} scale={0.72} fill="#15723a" vein="#06321d" className="svg-sway svg-sway-slow" />
                <BroadLeaf x={386} y={-20} rotate={88} scale={0.6} fill="#1d8c41" vein="#06321d" className="svg-sway" />
                <BroadLeaf x={568} y={-28} rotate={118} scale={0.5} fill="#2eb257" vein="#0a4527" className="svg-sway svg-sway-slow" />
                <BroadLeaf x={902} y={-22} rotate={96} scale={0.56} fill="#1d8c41" vein="#06321d" className="svg-sway" />
                <BroadLeaf x={1248} y={-26} rotate={112} scale={0.74} fill="#15723a" vein="#06321d" className="svg-sway svg-sway-slow" />
                <BroadLeaf x={1466} y={-14} rotate={136} scale={0.98} fill="#0f5f33" vein="#042415" className="svg-sway" />

                {/* drifting bits */}
                <g className="svg-float">
                    <ellipse cx="470" cy="196" rx="19" ry="9.5" fill="#9fe25f" stroke="#0a4527" strokeWidth="3" transform="rotate(-24 470 196)" opacity="0.9" />
                </g>
                <g className="svg-float svg-float-slow">
                    <ellipse cx="980" cy="178" rx="16" ry="8" fill="#c8f08a" stroke="#0a4527" strokeWidth="3" transform="rotate(32 980 178)" opacity="0.85" />
                </g>
            </svg>

            {/* ---- jungle floor, pinned to the bottom of the viewport ---- */}
            <svg
                className="scene-ground"
                viewBox="0 0 1440 620"
                preserveAspectRatio="xMidYMax slice"
                role="presentation"
            >
                <defs>
                    <linearGradient id="mist" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#d9f7ea" stopOpacity="0" />
                        <stop offset="100%" stopColor="#d9f7ea" stopOpacity="0.3" />
                    </linearGradient>
                </defs>

                {/* distant hills */}
                <path
                    d="M -40 300 C 180 228 320 274 520 300 C 700 322 900 256 1120 284 C 1280 302 1400 270 1480 300 L 1480 620 L -40 620 Z"
                    fill="#15723a"
                    opacity="0.5"
                />
                <path d="M -40 286 L 1480 352 L 1480 408 L -40 356 Z" fill="url(#mist)" opacity="0.6" />

                {/* far palms */}
                <g opacity="0.45">
                    <Palm x={168} y={330} scale={0.5} leaf="#15723a" leafAlt="#1d8c41" vein="#06321d" />
                    <Palm x={506} y={312} scale={0.42} flip leaf="#15723a" leafAlt="#1d8c41" vein="#06321d" />
                    <Palm x={962} y={322} scale={0.46} leaf="#15723a" leafAlt="#1d8c41" vein="#06321d" />
                    <Palm x={1286} y={308} scale={0.4} flip leaf="#15723a" leafAlt="#1d8c41" vein="#06321d" />
                </g>

                {/* mid ground ridge */}
                <path
                    d="M -40 420 C 240 372 420 428 640 420 C 880 410 1080 444 1480 402 L 1480 620 L -40 620 Z"
                    fill="#0a4527"
                    opacity="0.92"
                />

                <g opacity="0.85">
                    <Palm x={392} y={492} scale={0.62} leaf="#1d8c41" leafAlt="#2eb257" vein="#06321d" />
                    <Palm x={1046} y={500} scale={0.66} flip leaf="#1d8c41" leafAlt="#2eb257" vein="#06321d" withBananas />
                </g>

                {/* near palms framing the edges */}
                <Palm x={92} y={648} scale={1.04} leaf="#1d8c41" leafAlt="#2eb257" vein="#06321d" withBananas />
                <Palm x={1360} y={672} scale={1.14} flip leaf="#1d8c41" leafAlt="#2eb257" vein="#06321d" />

                {/* undergrowth along the bottom */}
                <BroadLeaf x={-30} y={620} rotate={-26} scale={0.88} fill="#1d8c41" vein="#06321d" />
                <BroadLeaf x={-14} y={638} rotate={4} scale={0.78} fill="#2eb257" vein="#0a4527" />
                <BroadLeaf x={1470} y={620} rotate={-154} scale={0.92} fill="#1d8c41" vein="#06321d" />
                <BroadLeaf x={1452} y={640} rotate={176} scale={0.8} fill="#2eb257" vein="#0a4527" />
                <BroadLeaf x={612} y={642} rotate={-34} scale={0.5} fill="#2eb257" vein="#0a4527" opacity={0.95} />
                <BroadLeaf x={842} y={648} rotate={-146} scale={0.46} fill="#55cf74" vein="#0a4527" opacity={0.95} />
                <BroadLeaf x={262} y={652} rotate={-150} scale={0.4} fill="#1d8c41" vein="#06321d" opacity={0.9} />
                <BroadLeaf x={1142} y={656} rotate={-30} scale={0.42} fill="#2eb257" vein="#0a4527" opacity={0.9} />

                {/* a few bananas resting in the undergrowth */}
                <BananaBunch x={214} y={596} scale={0.62} rotate={-12} />
            </svg>
        </div>
    );
}
